/**
 * Local file access, and the OS file-association entry point.
 *
 * Two ways a PDF reaches Odie from the local disk:
 *
 *  1. The user picks it (`showOpenFilePicker`).
 *  2. The user double-clicks a .pdf in Explorer and the OS launches the
 *     installed PWA, delivering the file through `window.launchQueue`.
 *
 * Both hand back a `FileSystemFileHandle`, not a `File`. That distinction is
 * the whole point: a handle can be written back to, so Save updates the
 * original file in place instead of dropping a copy in Downloads. Requires
 * Chromium desktop (Chrome / Edge / Brave), which is our target.
 */

export type FileSource = 'local' | 'onedrive'

export interface OpenedFile {
  name: string
  blob: Blob
  /** Present for local files; absent for OneDrive, which saves via Graph. */
  handle?: FileSystemFileHandle
  source: FileSource
  /** Graph driveItem id when source is 'onedrive'. */
  driveItemId?: string
  /** The drive it is in, and its version when opened: Save goes back only if
   *  nobody changed it since. */
  driveId?: string
  eTag?: string
}

/** Feature detection, so we can tell the user *why* rather than just failing. */
export const capabilities = {
  get fileSystemAccess(): boolean {
    return typeof window !== 'undefined' && 'showOpenFilePicker' in window
  },
  get fileHandling(): boolean {
    return typeof window !== 'undefined' && 'launchQueue' in window
  },
  get installed(): boolean {
    return (
      typeof window !== 'undefined' &&
      window.matchMedia('(display-mode: standalone)').matches
    )
  },
}

const PDF_PICKER_OPTIONS: OpenFilePickerOptions = {
  types: [{ description: 'PDF document', accept: { 'application/pdf': ['.pdf'] } }],
  excludeAcceptAllOption: false,
  multiple: false,
}

/** Prompt the user to choose a PDF from disk. */
export async function pickFile(): Promise<OpenedFile | null> {
  if (!capabilities.fileSystemAccess) {
    // Fall back to a plain input; Save then becomes "Save a copy".
    return pickFileLegacy()
  }
  try {
    const [handle] = await window.showOpenFilePicker(PDF_PICKER_OPTIONS)
    const blob = await handle.getFile()
    return { name: handle.name, blob, handle, source: 'local' }
  } catch (err) {
    // AbortError just means the user closed the dialog.
    if ((err as DOMException)?.name === 'AbortError') return null
    throw err
  }
}

function pickFileLegacy(): Promise<OpenedFile | null> {
  return new Promise((resolve) => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = 'application/pdf,.pdf'
    input.onchange = () => {
      const file = input.files?.[0]
      resolve(file ? { name: file.name, blob: file, source: 'local' } : null)
    }
    input.oncancel = () => resolve(null)
    input.click()
  })
}

/**
 * Register the editor for OS file-association launches.
 *
 * Files that arrived before the editor mounted -- including ones carried
 * across a sign-in redirect -- are delivered immediately.
 */
export function onFileLaunch(handler: (file: OpenedFile) => void): void {
  launchHandler = handler
  for (const file of launchBuffer.splice(0)) handler(file)
}

// The launch queue is consumed at startup, before sign-in, not when the editor
// mounts. Sign-in may have to leave the page for Microsoft's login screen, and
// a launch that has not been taken off the queue by then is simply gone -- the
// user double-clicks a PDF, signs in, and lands on an empty editor. Taking it
// early lets the auth layer carry it across the redirect (see auth/carry.ts).
const launchBuffer: OpenedFile[] = []
let launchHandler: ((file: OpenedFile) => void) | null = null
const launchWaiters: (() => void)[] = []

/** Hand a file to the editor, or hold it until the editor is ready. */
export function deliverLaunch(file: OpenedFile): void {
  if (launchHandler) launchHandler(file)
  else launchBuffer.push(file)
  for (const wake of launchWaiters.splice(0)) wake()
}

/** Start taking files off the launch queue. Call once, first thing. */
export function captureLaunches(): void {
  if (!capabilities.fileHandling) return

  window.launchQueue.setConsumer(async (params: LaunchParams) => {
    if (!params.files?.length) return
    for (const handle of params.files) {
      if (handle.kind !== 'file') continue
      const fileHandle = handle as FileSystemFileHandle
      const blob = await fileHandle.getFile()
      deliverLaunch({ name: fileHandle.name, blob, handle: fileHandle, source: 'local' })
    }
  })
}

/** Launched files not yet taken by the editor. */
export function pendingLaunches(): OpenedFile[] {
  return launchBuffer.slice()
}

/**
 * Resolve once a launch arrives, or after `ms`.
 *
 * Chrome delivers queued launches asynchronously after setConsumer, and the
 * consumer then has to read the file; on a slow disk that can lose a race
 * with a sign-in redirect started immediately on page load.
 */
export function waitForLaunch(ms: number): Promise<void> {
  if (launchBuffer.length) return Promise.resolve()
  return new Promise((resolve) => {
    const timer = window.setTimeout(resolve, ms)
    launchWaiters.push(() => {
      window.clearTimeout(timer)
      resolve()
    })
  })
}

/**
 * Write bytes back to the file the user opened.
 *
 * Returns false when we hold no handle (legacy picker or OneDrive), so the
 * caller can route to "Save a copy" or to Graph instead of silently doing
 * nothing.
 */
export async function saveInPlace(
  handle: FileSystemFileHandle | undefined,
  data: Blob,
): Promise<boolean> {
  if (!handle) return false

  // Write permission is granted separately from read, and only in response to
  // a user gesture. Ask once, then remember for the session.
  const options = { mode: 'readwrite' as const }
  if ((await handle.queryPermission(options)) !== 'granted') {
    if ((await handle.requestPermission(options)) !== 'granted') return false
  }

  // createWritable writes to a temp file and swaps on close, so an interrupted
  // save cannot truncate the user's original document.
  const writable = await handle.createWritable()
  try {
    await writable.write(data)
  } finally {
    await writable.close()
  }
  return true
}

/** Save As, for when there is no handle or the user wants a new file. */
export async function saveCopy(suggestedName: string, data: Blob): Promise<boolean> {
  if (!capabilities.fileSystemAccess) {
    downloadFallback(suggestedName, data)
    return true
  }
  try {
    const handle = await window.showSaveFilePicker({
      suggestedName,
      types: [
        { description: 'PDF document', accept: { 'application/pdf': ['.pdf'] } },
      ],
    })
    const writable = await handle.createWritable()
    try {
      await writable.write(data)
    } finally {
      await writable.close()
    }
    return true
  } catch (err) {
    if ((err as DOMException)?.name === 'AbortError') return false
    throw err
  }
}

function downloadFallback(name: string, data: Blob): void {
  const url = URL.createObjectURL(data)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = name
  anchor.click()
  // Revoke on the next tick; revoking immediately can cancel the download.
  setTimeout(() => URL.revokeObjectURL(url), 10_000)
}
